import { useMemo } from 'react';
import { isValidProductLevel } from '../../utils/typeguards';
import { useProductInventory } from '../inventory/useProductInventory';
const orderedProductLevels = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];
export const calculateHighestProductLevel = productLevels => {
  const validLevels = productLevels.filter(isValidProductLevel);
  if (validLevels.length === 0) {
    return 'FREE';
  }
  const maxIndex = Math.max(0, ...validLevels.map(level => orderedProductLevels.indexOf(level)));
  return orderedProductLevels[maxIndex];
};

/**
 * Compares multiple product levels with their sources and returns the highest level along with metadata.
 * Useful for determining which source provides the highest product level access.
 *
 * @param inputs - Array of objects containing product level and its source identifier
 * @returns Object containing the highest level, its source, count of valid inputs, and all sources
 * @throws {Error} When no inputs are provided
 */
export const compareProductLevels = inputs => {
  if (inputs.length === 0) {
    throw new Error('compareProductLevels requires at least one input');
  }

  // Filter to only valid inputs and get their indices
  const validInputs = inputs.map((input, index) => Object.assign({}, input, {
    originalIndex: index,
    validLevel: isValidProductLevel(input.level) ? input.level : null
  })).filter(input => input.validLevel !== null);

  // If no valid inputs, return FREE with first source
  if (validInputs.length === 0) {
    return {
      higherLevel: 'FREE',
      source: inputs[0].source,
      validInputsCount: 0,
      allSources: inputs.map(input => input.source)
    };
  }

  // Find the highest level
  let highestInput = validInputs[0];
  let highestIndex = orderedProductLevels.indexOf(highestInput.validLevel);
  validInputs.forEach(input => {
    const currentIndex = orderedProductLevels.indexOf(input.validLevel);
    if (currentIndex > highestIndex) {
      highestIndex = currentIndex;
      highestInput = input;
    }
  });
  return {
    higherLevel: highestInput.validLevel,
    source: highestInput.source,
    validInputsCount: validInputs.length,
    allSources: inputs.map(input => input.source)
  };
};
export const useHighestProductLevelFromInventory = ({
  skuCategoryName,
  skip = false
} = {}) => {
  const {
    highestProductLevelOwned,
    products,
    loading: inventoryLoading,
    error: inventoryError
  } = useProductInventory({
    skip
  });

  // Filter products by skuCategoryName once and reuse
  const filteredProducts = useMemo(() => {
    if (!(products !== null && products !== void 0 && products.length)) return [];
    return skuCategoryName ? products.filter(product => product.skuCategoryName === skuCategoryName) : products;
  }, [products, skuCategoryName]);
  const highestProductLevel = useMemo(() => {
    if (inventoryLoading) {
      return null;
    }
    if (inventoryError) {
      return 'FREE';
    }

    // don't use highestProductLevelOwned if skuCategoryName is provided,
    if (!skuCategoryName && highestProductLevelOwned && isValidProductLevel(highestProductLevelOwned)) {
      return highestProductLevelOwned;
    }
    if (!(filteredProducts !== null && filteredProducts !== void 0 && filteredProducts.length)) {
      return 'FREE';
    }
    const productLevels = filteredProducts.map(product => product.productLevel);
    return calculateHighestProductLevel(productLevels);
  }, [inventoryLoading, inventoryError, skuCategoryName, highestProductLevelOwned, filteredProducts]);
  const loading = inventoryLoading;
  const error = inventoryError;
  return {
    highestProductLevel,
    loading,
    error
  };
};

/**
 * Hook to check if user has specified tier or higher.
 * Can be filtered by SkuCategoryName.
 */
export const useHasProductLevelOrHigherInProductInventory = ({
  productLevel,
  skuCategoryName
}) => {
  const {
    highestProductLevel,
    loading,
    error
  } = useHighestProductLevelFromInventory({
    skuCategoryName
  });
  const hasProductLevelOrHigher = useMemo(() => {
    if (loading) {
      return undefined;
    }
    if (!highestProductLevel) {
      return false;
    }
    return orderedProductLevels.indexOf(highestProductLevel) >= orderedProductLevels.indexOf(productLevel);
  }, [highestProductLevel, productLevel, loading]);
  return {
    hasProductLevelOrHigher,
    loading,
    error
  };
};

/**
 * Hook to check if user has higher tier than specified.
 * Can be filtered by SkuCategoryName.
 */
export const useHasHigherProductLevelInProductInventory = ({
  productLevel,
  skuCategoryName,
  skip = false
}) => {
  const {
    highestProductLevel,
    loading,
    error
  } = useHighestProductLevelFromInventory({
    skuCategoryName,
    skip
  });
  const hasHigherProductLevel = useMemo(() => {
    if (loading) {
      return undefined;
    }
    if (!highestProductLevel) {
      return false;
    }
    const levelToCompare = isValidProductLevel(productLevel) ? productLevel : 'FREE';
    return orderedProductLevels.indexOf(highestProductLevel) > orderedProductLevels.indexOf(levelToCompare);
  }, [highestProductLevel, productLevel, loading]);
  return {
    hasHigherProductLevel,
    loading,
    error
  };
};